import { walkDOM } from '../utils/dom'
import { camel, snake } from '../utils/text'
import { DATASTAR, DSP, DSS } from './consts'
import { initErr, runtimeErr } from './errors'
import {
  computed,
  dep,
  depPaths,
  effect,
  signal,
  batch,
  evalRX,
} from './signals'
import {
  type ActionPlugins,
  type AttributePlugin,
  type DatastarPlugin,
  type Dependency,
  type HTMLOrSVGElement,
  type InitContext,
  type OnRemovalFn,
  PluginType,
  Requirement,
  type RuntimeContext,
  type RuntimeExpressionFunction,
} from './types'

const actions: ActionPlugins = {}
const plugins: AttributePlugin[] = []

// Map of cleanup functions by element, keyed by a dataset key-value hash
const removals = new Map<HTMLOrSVGElement, Map<string, OnRemovalFn>>()

let mutationObserver: MutationObserver | null = null

let alias = ''
export function setAlias(value: string) {
  alias = value
}

export function load(...pluginsToLoad: DatastarPlugin[]) {
  for (const plugin of pluginsToLoad) {
    const ctx: InitContext = {
      plugin,
      actions,
      removals,
      applyToElement,
      batch,
      signal,
      computed,
      effect,
    }

    const type = plugin.type
    if (type === PluginType.Action) {
      actions[plugin.name] = plugin
    } else if (type === PluginType.Attribute) {
      plugins.push(plugin)
      plugin.onGlobalInit?.(ctx)
    } else if (type === PluginType.Watcher) {
      plugin.onGlobalInit?.(ctx)
    } else {
      throw initErr('InvalidPluginType', ctx)
    }
  }

  // Sort attribute plugins by descending length then alphabetically
  plugins.sort((a, b) => {
    const lenDiff = b.name.length - a.name.length
    if (lenDiff !== 0) return lenDiff
    return a.name.localeCompare(b.name)
  })
}

// Apply all plugins to all elements in the DOM
export function apply() {
  // Delay applying plugins to give custom plugins a chance to load
  queueMicrotask(() => {
    walkDOM(document.documentElement, applyToElement)

    // Monitor the entire document body for changes
    // https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver/observe
    mutationObserver = new MutationObserver(observe)
    // Run the observer once to start listening
    observe([])
  })
}

// Set up a mutation observer to run plugin removal and apply functions
function observe(mutations: MutationRecord[]) {
  // Don't track mutations because we cause mutations inside of this callback. Without this, we
  // would get an infinite loop
  mutationObserver!.disconnect()

  for (const { target, type, addedNodes, removedNodes } of mutations) {
    switch (type) {
      case 'childList':
        {
          // We don't care about the order of removals
          for (const node of removedNodes) {
            walkDOM(node, (el) => {
              const elCleanups = removals.get(el)
              // If removals has el, delete it and run all cleanup functions
              if (removals.delete(el)) {
                for (const cleanup of elCleanups!.values()) {
                  cleanup()
                }
                elCleanups!.clear()
              }
            })
          }
          for (const node of addedNodes) {
            // Set the `data-apply` attribute on all new nodes so we can come back and apply plugins
            // in the order they appear in the dom
            walkDOM(node, (el) => {
              el.setAttribute('data-apply', '')
            })
          }
        }
        break
      case 'attributes': {
        ;(target as HTMLOrSVGElement).setAttribute('data-apply', '')
        break
      }
    }
  }

  // `querySelectorAll` will collect all elements with the `data-apply` attribute in the order they
  // appear in the dom
  for (const el of document.querySelectorAll('[data-apply]')) {
    el.removeAttribute('data-apply')
    applyToElement(el as HTMLOrSVGElement)
  }

  // Start tracking again
  mutationObserver!.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
  })
}

// use djb2 favored by bernstein http://www.cse.yorku.ca/~oz/hash.html
function djb2(str: string) {
  let hash = 5831
  let i = str.length
  while (i--) {
    hash += (hash << 5) + str.charCodeAt(i)
  }
  return (hash >>> 0).toString(36)
}

type ToApply = {
  key: string
  value: string
  hash: string
}

// Apply all plugins to the element
function applyToElement(el: HTMLOrSVGElement) {
  // Check if the element has any data attributes already
  const toApply: ToApply[] = []
  const elCleanups = removals.get(el) || new Map()
  const toCleanup = new Map(elCleanups)

  // Apply the plugins to the element in order of application
  // since DOMStringMap is ordered, we can be deterministic
  for (const [key, value] of Object.entries(el.dataset) as [string, string][]) {
    // Ignore data attributes that don’t start with the alias
    if (!key.startsWith(alias)) {
      continue
    }

    const hash = djb2(`${key}${value}`)

    // If toCleanup has hash, delete it, else push to toApply
    if (!toCleanup.delete(hash)) {
      toApply.push({ key, value, hash })
    }
  }

  // Cleanup the left over plugins and delete them from elCleanups
  for (const [hash, cleanup] of toCleanup) {
    cleanup()
    elCleanups.delete(hash)
  }

  for (const { key, value, hash } of toApply) {
    // Apply the plugin to the element
    const cleanup = applyAttributePlugin(el, key, value)
    if (cleanup) {
      // Store the cleanup function
      elCleanups.set(hash, cleanup)
    }
  }
  // If elCleanups has any cleanups, set it in removals
  if (elCleanups.size) {
    removals.set(el, elCleanups)
  }
}

function applyAttributePlugin(
  el: HTMLOrSVGElement,
  datasetKey: string,
  value: string,
) {
  // Extract the raw key from the dataset
  const rawKey = camel(datasetKey.slice(alias.length))

  // Find the plugin that matches, since the plugins are sorted by length descending and alphabetically. The first match will be the most specific.
  // Ignore keys with the plugin name as a prefix (ignores `classes` but not `classBold`)
  const plugin = plugins.find((p) =>
    RegExp(`^${p.name}([A-Z]|_|$)`).test(rawKey),
  )

  // Skip if no plugin is found
  if (!plugin) return

  // Extract the key and modifiers
  let [key, ...rawModifiers] = rawKey.slice(plugin.name.length).split(/\_\_+/)

  const hasKey = !!key
  if (hasKey) {
    key = camel(key)
  }
  const hasValue = !!value

  // Create the runtime context
  const ctx: RuntimeContext = {
    applyToElement,
    actions,
    removals,
    genRX: () => genRX(ctx, ...(plugin.argNames || [])),
    plugin,
    el,
    rawKey,
    key,
    value,
    mods: new Map(),
    batch,
    signal,
    computed,
    effect,
    evalRX,
  }

  // Check the requirements
  const keyReq = plugin.keyReq || Requirement.Allowed
  if (hasKey) {
    if (keyReq === Requirement.Denied) {
      throw runtimeErr(`${plugin.name}KeyNotAllowed`, ctx)
    }
  } else if (keyReq === Requirement.Must) {
    throw runtimeErr(`${plugin.name}KeyRequired`, ctx)
  }

  const valReq = plugin.valReq || Requirement.Allowed
  if (hasValue) {
    if (valReq === Requirement.Denied) {
      throw runtimeErr(`${plugin.name}ValueNotAllowed`, ctx)
    }
  } else if (valReq === Requirement.Must) {
    throw runtimeErr(`${plugin.name}ValueRequired`, ctx)
  }

  // Check for exclusive requirements
  if (keyReq === Requirement.Exclusive || valReq === Requirement.Exclusive) {
    if (hasKey && hasValue) {
      throw runtimeErr(`${plugin.name}KeyAndValueProvided`, ctx)
    }
    if (!hasKey && !hasValue) {
      throw runtimeErr(`${plugin.name}KeyOrValueRequired`, ctx)
    }
  }

  for (const rawMod of rawModifiers) {
    const [label, ...mod] = rawMod.split('.')
    ctx.mods.set(camel(label), new Set(mod.map((t) => t.toLowerCase())))
  }

  // Load the plugin and return the cleanup function
  return plugin.onLoad(ctx)
}

function genRX(
  ctx: RuntimeContext,
  ...argNames: string[]
): {
  deps: Dependency[]
  dm: boolean[] // dependency mutable flags
  rxFn: RuntimeExpressionFunction
} {
  const dm: boolean[] = []
  const dependencySet = new Set<Dependency>()
  let expr = ''

  // This regex allows Datastar expressions to support nested
  // regex and strings that contain ; without breaking.
  //
  // Each of these regex defines a block type we want to match
  // (importantly we ignore the content within these blocks):
  //
  // regex            \/(\\\/|[^\/])*\/
  // double quotes      "(\\"|[^\"])*"
  // single quotes      '(\\'|[^'])*'
  // ticks              `(\\`|[^`])*`
  // iife               \(\s*((function)\s*\(\s*\)|(\(\s*\))\s*=>)\s*(?:\{[\s\S]*?\}|[^;)\{]*)\s*\)\s*\(\s*\)
  //
  // The iife support is (intentionally) limited. It only supports
  // function and arrow syntax with no arguments, and no nested IIFEs.
  //
  // We also want to match the non delimiter part of statements
  // note we only support ; statement delimiters:
  //
  // [^;]
  //
  const statementRe =
    /(\/(\\\/|[^\/])*\/|"(\\"|[^\"])*"|'(\\'|[^'])*'|`(\\`|[^`])*`|\(\s*((function)\s*\(\s*\)|(\(\s*\))\s*=>)\s*(?:\{[\s\S]*?\}|[^;)\{]*)\s*\)\s*\(\s*\)|[^;])+/gm
  const statements = ctx.value.trim().match(statementRe)
  if (statements) {
    const lastIdx = statements.length - 1
    const last = statements[lastIdx].trim()
    if (!last.startsWith('return')) {
      statements[lastIdx] = `return (${last});`
    }
    expr = statements.join(';\n')
  }

  // Ignore any escaped values
  const escaped = new Map<string, string>()
  const escapeRe = RegExp(`(?:${DSP})(.*?)(?:${DSS})`, 'gm')
  for (const match of expr.matchAll(escapeRe)) {
    const k = match[1]
    const v = `dsEscaped${djb2(k)}`
    escaped.set(v, k)
    expr = expr.replace(DSP + k + DSS, v)
  }

  const nameGen = (prefix: string, name: string) => {
    return `${prefix}${snake(name).replaceAll(/\./g, '_')}`
  }

  const argsDependenciesActionsNames = new Set<string>(argNames)
  const dependencyPaths = depPaths()
  if (dependencyPaths.length) {
    const pattern = dependencyPaths
      .toSorted((a, b) => b.length - a.length)
      .join('|')

    const signalsWithAssignedValuesRe = RegExp(
      `\\$(${pattern})(\\s*[+&^\\/*|-]?=[^=]|\\+\\+|--)`,
      'gm',
    )
    const signalsWithAssignedValuesMatches = [
      ...expr.matchAll(signalsWithAssignedValuesRe),
    ]

    const updateUserExpression = (
      match: RegExpExecArray,
      name: string,
      suffix = '',
    ) => {
      expr = expr.replaceAll(`$${match[1]}`, name + suffix)
    }

    if (signalsWithAssignedValuesMatches.length) {
      const signalMutationPrefix = `${DATASTAR}Mut_`
      const mutableDependencies = new Set<Dependency>()
      for (const match of signalsWithAssignedValuesMatches) {
        const depName = match[1]
        const d = dep(depName)
        const name = nameGen(signalMutationPrefix, depName)
        if (d && !mutableDependencies.has(d)) {
          dependencySet.add(d)
          mutableDependencies.add(d)
          dm.push(true)
          argsDependenciesActionsNames.add(name)
        }
        updateUserExpression(match, `${name}`, '.value')
      }
    }

    const pureDependencyRe = RegExp(`\\$(${pattern})(\\W|$)`, 'gm')
    const allPureDependenciesMatches = [...expr.matchAll(pureDependencyRe)]
    if (allPureDependenciesMatches.length) {
      const pureDependencyPrefix = `${DATASTAR}Pure_`
      const pureDependencies = new Set<Dependency>()
      for (const match of allPureDependenciesMatches) {
        const pureDepName = match[1]
        const pureDep = dep(pureDepName)
        const name = nameGen(pureDependencyPrefix, pureDepName)
        if (pureDep && !pureDependencies.has(pureDep)) {
          dependencySet.add(pureDep)
          pureDependencies.add(pureDep)
          dm.push(false)
          argsDependenciesActionsNames.add(name)
        }
        updateUserExpression(match, name)
      }
    }
  }

  // Replace any action calls
  const actionsCalled = new Set<string>()
  const actionsRe = RegExp(`@(${Object.keys(actions).join('|')})\\(`, 'gm')
  const actionMatches = [...expr.matchAll(actionsRe)]
  const actionFns = new Set<(...args: any[]) => any>()
  if (actionMatches.length) {
    const actionPrefix = `${DATASTAR}Act_`
    for (const match of actionMatches) {
      const actionName = match[1]
      const action = actions[actionName]
      if (!action) {
        continue
      }
      actionsCalled.add(actionName)

      const name = nameGen(actionPrefix, actionName)
      argsDependenciesActionsNames.add(name)
      // Add ctx to action calls
      expr = expr.replace(`@${actionName}(`, `${name}(`)
      actionFns.add((...args: any[]) => action.fn(ctx, ...args))
    }
  }

  // Replace any escaped values
  for (const [k, v] of escaped) {
    expr = expr.replace(k, v)
  }

  ctx.fnContent = expr

  try {
    const fn = Function('el', ...argsDependenciesActionsNames, expr)
    return {
      dm,
      deps: [...dependencySet],
      rxFn: (...argsAndDependencyValues: any[]) => {
        try {
          return fn(ctx.el, ...argsAndDependencyValues, ...actionFns)
        } catch (e: any) {
          throw runtimeErr('ExecuteExpression', ctx, {
            error: e.message,
          })
        }
      },
    }
  } catch (error: any) {
    throw runtimeErr('GenerateExpression', ctx, {
      error: error.message,
    })
  }
}
